import { useMemo } from "react";
import "./Snowfall.scss";

interface SnowflakeConfig {
  id: number;
  size: number; // px, up to maxSize
  left: number; // % across screen
  duration: number; // ms fall duration (mirrors mobile duration prop)
  delay: number; // ms stagger so flakes don't all start together
  drift: number; // px horizontal drift during fall
  opacity: number;
}

interface SnowfallProps {
  count?: number; // number of flakes (mobile renders ~30 by default)
  maxSize?: number; // mobile: maxSize={10}
  duration?: number; // mobile: duration={6000}
}

function Snowfall({
  count = 30,
  maxSize = 10,
  duration = 6000,
}: SnowfallProps) {
  const flakes = useMemo<SnowflakeConfig[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      size: Math.random() * (maxSize - 3) + 3, // 3..maxSize px
      left: Math.random() * 100, // 0..100%
      duration: duration * (0.7 + Math.random() * 0.6), // ±30% of base duration
      delay: -(Math.random() * duration), // negative = already mid-fall
      drift: (Math.random() - 0.5) * 60, // -30..+30px horizontal drift
      opacity: 0.5 + Math.random() * 0.5, // 0.5..1.0
    }));
  }, [count, maxSize, duration]);

  return (
    <div className="snowfall" aria-hidden="true">
      {flakes.map((f) => (
        <div
          key={f.id}
          className="snowfall__flake"
          style={
            {
              width: f.size,
              height: f.size,
              left: `${f.left}%`,
              opacity: f.opacity,
              animationDuration: `${f.duration}ms`,
              animationDelay: `${f.delay}ms`,
              "--drift": `${f.drift}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

export default Snowfall;
